// Plain-English validation and error text for the Create-account form.
//
// GoTrue's own strings leak implementation detail at exactly the moment a new
// player is least able to act on it: a mistyped address comes back as
// "Unable to validate email address: invalid format", a short one as
// "Password should be at least 6 characters.", an empty one as the
// back-to-front "Signup requires a valid password" — and a project that sets
// required characters answers with a wall of punctuation. The panel printed
// whichever it got, verbatim.
//
// Two halves fix that: `validateEmail`/`validatePassword` catch the same
// mistakes BEFORE the network call so the form answers instantly, and
// `friendlySignupError` rewrites what still comes back from the server.
// Anything unrecognised passes through unchanged — a message we have not seen
// is more useful raw than hidden behind "something went wrong".
//
// Pure and unit-tested, like authError.ts beside it.

/**
 * Mirrors `minimum_password_length` in supabase/config.toml and the hosted auth
 * config on qa and prod (all three are 6). Keep them in sync — the server is
 * the authority, and `friendlySignupError` quotes ITS number if they ever drift.
 */
export const PASSWORD_MIN = 6;

/** Shown under the password field, so the rule arrives before the mistake. */
export const PASSWORD_HINT = `At least ${PASSWORD_MIN} characters.`;

const BAD_EMAIL = 'That doesn’t look like an email address';
const NO_PASSWORD = 'Enter a password';

/**
 * Deliberately loose — something, an @, something, a dot, something, and no
 * spaces. The server does the real validation; this only has to catch the
 * typos worth catching without ever rejecting an address GoTrue would accept.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEmail(email: string): string | null {
  const value = email.trim();
  if (!value) return 'Enter your email address';
  return EMAIL_RE.test(value) ? null : BAD_EMAIL;
}

export function validatePassword(password: string): string | null {
  if (!password) return NO_PASSWORD;
  if (password.length < PASSWORD_MIN) {
    return `Your password needs at least ${PASSWORD_MIN} characters`;
  }
  return null;
}

// Two doors lead here: GoTrue's explicit "already registered" (local stack)
// and hosted GoTrue's fake-success user with no identities (enumeration
// protection on) — register() catches the second. One sentence for both.
export const ALREADY_REGISTERED = 'That email already has an account — try signing in instead';

export function friendlySignupError(raw: string): string {
  if (/unable to validate email address/i.test(raw)) return BAD_EMAIL;
  if (/signup requires a valid password/i.test(raw)) return NO_PASSWORD;

  // Quote the server's own minimum rather than PASSWORD_MIN: if the hosted
  // config is ever raised without this file, the player still reads the truth.
  const short = /password should be at least (\d+) characters/i.exec(raw);
  if (short) return `Your password needs at least ${short[1]} characters`;

  if (/password should contain at least one character of each/i.test(raw)) {
    return 'Your password needs upper- and lower-case letters, a digit and a symbol';
  }

  if (/already registered/i.test(raw)) return ALREADY_REGISTERED;

  const wait = /you can only request this after (\d+) seconds/i.exec(raw);
  if (wait) return `Too many tries just now — wait ${wait[1]} seconds and try again`;
  if (/rate limit/i.test(raw)) return 'Too many emails just now — wait a minute and try again';

  // The profile trigger's failures reach the client as a generic database
  // error. The caller has already told "username taken" apart from real
  // trouble, so anything still wearing this text is trouble the player cannot
  // act on — and "Database error saving new user" only alarms them.
  if (/database error/i.test(raw)) {
    return 'Something went wrong creating your account. Please try again.';
  }

  return raw;
}
